import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ShortcutKeysCatalog,
  ShortcutKeysPageEntry,
} from '../models/shortcut-keys.models';
import { ShortcutKeysRouterPath } from '../utility/constants';

@Injectable({ providedIn: 'root' })
export class ShortcutKeysCatalogService {
  private readonly http = inject(HttpClient);
  private cache$: Observable<ShortcutKeysCatalog> | null = null;

  public getCatalog(): Observable<ShortcutKeysCatalog> {
    if (!this.cache$) {
      this.cache$ = this.http
        .get<ShortcutKeysCatalog>(environment.azureCDNShortcutKeysCatalog)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.cache$;
  }

  public findPageInCatalog(
    catalog: ShortcutKeysCatalog,
    pageId: string
  ): ShortcutKeysPageEntry | null {
    for (const mod of catalog.modules) {
      const p = mod.pages.find(page => page.id === pageId);
      if (p) {
        return p;
      }
    }
    return null;
  }

  public findPageIdForRouterUrl(routerUrl: string): string | null {
    const segments = this.pathSegmentsFromRouterUrl(routerUrl);
    if (segments.length === 0) {
      return null;
    }
    const r = ShortcutKeysRouterPath;
    if (segments[0] === r.docsFeature) {
      return null;
    }
    return this.pageIdForTopLevelRoute(segments);
  }

  private pageIdForTopLevelRoute(segments: string[]): string | null {
    const r = ShortcutKeysRouterPath;
    const first = segments[0];
    if (first === r.hawb) {
      return this.pageIdForHawbRoute(segments, r);
    }
    if (first === r.bag) {
      return this.pageIdForBagRoute(segments, r);
    }
    if (first === r.manifest) {
      return this.pageIdForManifestRoute(segments, r);
    }
    if (first === r.mawb) {
      return this.pageIdForMawbRoute(segments, r);
    }
    return null;
  }

  private pageIdForHawbRoute(
    segments: string[],
    r: typeof ShortcutKeysRouterPath
  ): string | null {
    if (segments.length === 1) {
      return r.hawbSummary;
    }
    if (segments[1] === r.hawbInformation) {
      return r.hawbInformation;
    }
    return null;
  }

  private pageIdForBagRoute(
    segments: string[],
    r: typeof ShortcutKeysRouterPath
  ): string | null {
    if (segments.length === 1 || segments[1] === r.bagSummary) {
      return r.bagSummary;
    }
    if (segments[1] === r.bagInformation) {
      return r.bagInformation;
    }
    return null;
  }

  private pageIdForManifestRoute(
    segments: string[],
    r: typeof ShortcutKeysRouterPath
  ): string | null {
    if (segments.length === 1) {
      return r.manifestSummary;
    }
    if (segments[1] === r.manifestInformation) {
      return r.manifestInformation;
    }
    return null;
  }

  private pageIdForMawbRoute(
    segments: string[],
    r: typeof ShortcutKeysRouterPath
  ): string | null {
    if (segments.length === 1) {
      return r.mawbSummary;
    }
    if (segments[1] === r.mawbInformation) {
      return r.mawbInformation;
    }
    return null;
  }

  private pathSegmentsFromRouterUrl(routerUrl: string): string[] {
    const pathOnly =
      (routerUrl.split('?')[0] ?? '').split('#')[0] ?? '';
    return pathOnly.split('/').filter(s => s.length > 0);
  }
}
